import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  UserIcon,
  ChevronDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { tasksAPI, usersAPI, teamsAPI, projectsAPI, authAPI } from '../../services/api';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TaskDetailModal from '../../components/admin/TaskDetailModal';
import TaskModal from '../../components/admin/TaskModal';
import MemberFilter from '../../components/calendar/MemberFilter';
import DayTasksModal from '../../components/calendar/DayTasksModal';
import toast from 'react-hot-toast';

interface CalendarTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  creator: any;
  assignees: any[];
  team?: any;
  type: 'due' | 'created' | 'recurring';
  time: string;
  isRecurringInstance?: boolean;
  recurringTemplateId?: string;
  recurrenceType?: string;
}

interface MonthData {
  year: number;
  month: number;
  tasksByDate: Record<string, CalendarTask[]>;
  totalTasks: number;
}

const AdminCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthData, setMonthData] = useState<MonthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  // Enhanced features state
  const [selectedMember, setSelectedMember] = useState<string>('all');
  const [users, setUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [draggedTask, setDraggedTask] = useState<any>(null);
  const [isTaskLoading, setIsTaskLoading] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Day tasks modal state
  const [isDayTasksModalOpen, setIsDayTasksModalOpen] = useState(false);
  const [selectedDayTasks, setSelectedDayTasks] = useState<CalendarTask[]>([]);
  const [selectedDayDate, setSelectedDayDate] = useState<string>('');

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      console.log('Loading initial data...');
      const [usersData, teamsData, projectsData, currentUserData] = await Promise.all([
        usersAPI.getAll(),
        teamsAPI.getAll(),
        projectsAPI.getAll({ isArchived: false }),
        authAPI.getCurrentUser()
      ]);
      console.log('Users loaded:', usersData);
      console.log('Teams loaded:', teamsData);
      console.log('Projects loaded:', projectsData);
      console.log('Current user loaded:', currentUserData);
      setUsers(usersData);
      setTeams(teamsData);
      setProjects(projectsData);
      setCurrentUser(currentUserData);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load initial data');
    }
  };

  const loadMonthData = useCallback(async (forceMember?: string) => {
    const memberToUse = forceMember || selectedMember;
    try {
      setIsLoading(true);
      console.log('🔄 [loadMonthData] Starting with params:', {
        memberToUse,
        selectedMember,
        currentYear,
        currentMonth,
        forceMember
      });

      // Note: Removed automatic recurring task generation from here to prevent
      // duplicate tasks being created on every calendar load

      let data;
      let apiUrl;

      if (memberToUse === 'all') {
        console.log('📊 [loadMonthData] Loading ALL tasks');
        apiUrl = `/tasks/calendar/month/${currentYear}/${currentMonth}`;
        data = await tasksAPI.getMonthTasks(currentYear, currentMonth);
      } else {
        console.log('👤 [loadMonthData] Loading tasks for specific member:', memberToUse);
        apiUrl = `/tasks/calendar/month/${currentYear}/${currentMonth}?assigneeId=${memberToUse}`;
        data = await tasksAPI.getMonthTasks(currentYear, currentMonth, memberToUse);
      }

      console.log('🌐 [loadMonthData] API URL:', apiUrl);
      console.log('✅ [loadMonthData] Data received:', data);
      console.log('📊 [loadMonthData] Total tasks:', data?.totalTasks || 0);
      console.log('📅 [loadMonthData] Task dates:', Object.keys(data?.tasksByDate || {}));

      setMonthData(data);

      // Log the state after setting
      setTimeout(() => {
        console.log('🔍 [loadMonthData] State after update - monthData set');
      }, 100);

    } catch (error) {
      console.error('❌ [loadMonthData] Error:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setIsLoading(false);
      console.log('🏁 [loadMonthData] Finished loading');
    }
  }, [selectedMember, currentYear, currentMonth]);

  // Load calendar data when dependencies change
  useEffect(() => {
    console.log('🔄 [useEffect] Triggered with dependencies:', {
      selectedMember,
      currentYear,
      currentMonth,
      forceRefresh
    });
    loadMonthData();
  }, [loadMonthData, forceRefresh]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleTaskClick = async (task: CalendarTask) => {
    try {
      if (task.type === 'recurring') {
        // For recurring tasks, show a message or redirect to recurring task management
        toast(`This is a recurring task (${task.recurrenceType?.toLowerCase()}). Manage it from the Recurring Tasks section.`, {
          icon: 'ℹ️',
          duration: 4000,
        });
        return;
      }

      // Get full task details for regular tasks
      const fullTask = await tasksAPI.getById(task.id);
      setSelectedTask(fullTask);
      setIsTaskModalOpen(true);
    } catch (error) {
      console.error('Error loading task details:', error);
      toast.error('Failed to load task details');
    }
  };

  const handleDateClick = (dateStr: string) => {
    const tasksForDay = monthData?.tasksByDate[dateStr] || [];
    setSelectedDayDate(dateStr);
    setSelectedDayTasks(tasksForDay);
    setIsDayTasksModalOpen(true);
  };

  const handleCreateTaskClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setIsCreateTaskModalOpen(true);
  };

  const handleMemberChange = async (memberId: string) => {
    console.log('🔄 [handleMemberChange] Member changed to:', memberId);
    console.log('🔄 [handleMemberChange] Previous member was:', selectedMember);

    // Update state immediately
    setSelectedMember(memberId);

    // Show immediate feedback
    const memberName = memberId === 'all' ? 'All Members' :
      users.find(u => u.id === memberId)?.firstName + ' ' + users.find(u => u.id === memberId)?.lastName;

    console.log('🔄 [handleMemberChange] Member name:', memberName);

    // DIRECTLY call the API with the new member ID - bypass all state dependencies
    try {
      setIsLoading(true);
      setMonthData(null); // Clear current data immediately

      console.log('🚀 [handleMemberChange] Making DIRECT API call for member:', memberId);

      let data;
      if (memberId === 'all') {
        console.log('📊 [handleMemberChange] DIRECT call for ALL tasks');
        data = await tasksAPI.getMonthTasks(currentYear, currentMonth);
      } else {
        console.log('👤 [handleMemberChange] DIRECT call for member tasks:', memberId);
        data = await tasksAPI.getMonthTasks(currentYear, currentMonth, memberId);
      }

      console.log('✅ [handleMemberChange] DIRECT API response:', data);
      console.log('📊 [handleMemberChange] DIRECT API task count:', data?.totalTasks || 0);

      setMonthData(data);
      toast.success(`Switched to ${memberName} - ${data?.totalTasks || 0} tasks found`);

    } catch (error) {
      console.error('❌ [handleMemberChange] DIRECT API error:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTaskDragStart = (e: React.DragEvent<HTMLDivElement>, task: CalendarTask) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDateDrop = async (e: React.DragEvent<HTMLDivElement>, dateStr: string) => {
    e.preventDefault();
    // Clean up visual feedback
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');

    if (!draggedTask) return;

    try {
      setIsTaskLoading(true);
      const newDate = new Date(dateStr);

      // Update task with new date
      await tasksAPI.update(draggedTask.id, {
        dueDate: newDate.toISOString(),
        startDate: draggedTask.startDate || newDate.toISOString()
      });

      toast.success('Task date updated successfully');
      loadMonthData();
    } catch (error) {
      console.error('Error updating task date:', error);
      toast.error('Failed to update task date');
    } finally {
      setIsTaskLoading(false);
      setDraggedTask(null);
    }
  };

  const handleDateDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Add visual feedback for valid drop zone
    e.currentTarget.classList.add('border-blue-400', 'bg-blue-50');
  };

  const handleDateDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Remove visual feedback when leaving drop zone
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
  };

  const handleTaskCreated = () => {
    setIsCreateTaskModalOpen(false);
    loadMonthData();
    toast.success('Task created successfully');
  };

  const handleTaskUpdated = () => {
    setIsTaskModalOpen(false);
    loadMonthData();
    toast.success('Task updated successfully');
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="min-h-32 p-2 border border-gray-200 bg-gray-50" />
      );
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const tasksForDay = monthData?.tasksByDate[dateStr] || [];
      const isToday = new Date().toDateString() === new Date(currentYear, currentMonth - 1, day).toDateString();

      // Debug logging for tasks
      if (tasksForDay.length > 0) {
        console.log(`Tasks for ${dateStr}:`, tasksForDay.length, tasksForDay);
      }

      days.push(
        <motion.div
          key={day}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2, delay: day * 0.01 }}
          className={`min-h-32 p-2 border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer relative group ${
            isToday ? 'ring-2 ring-primary-500 bg-primary-50' : ''
          } ${draggedTask ? 'hover:bg-blue-50 hover:border-blue-300' : ''}`}
          onClick={() => handleDateClick(dateStr)}
          onDrop={(e: React.DragEvent<HTMLDivElement>) => handleDateDrop(e, dateStr)}
          onDragOver={(e: React.DragEvent<HTMLDivElement>) => handleDateDragOver(e)}
          onDragLeave={(e: React.DragEvent<HTMLDivElement>) => handleDateDragLeave(e)}
        >
          <div className={`flex items-center justify-between mb-2`}>
            <div className={`text-sm font-medium ${isToday ? 'text-primary-600' : 'text-gray-900'}`}>
              {day}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCreateTaskClick(dateStr);
              }}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 hover:bg-primary-100 hover:text-primary-600 transition-colors opacity-0 group-hover:opacity-100"
              title="Add task"
            >
              <PlusIcon className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-1">
            {tasksForDay.slice(0, 3).map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                className="relative"
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTaskClick(task);
                  }}
                  draggable
                  onDragStart={(e: React.DragEvent<HTMLDivElement>) => handleTaskDragStart(e, task)}
                  className={`text-xs p-1 rounded border cursor-move hover:shadow-sm transition-all ${getStatusColor(task.status)} ${
                    draggedTask?.id === task.id ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                    <span className="truncate flex-1">{task.title}</span>
                    {task.type === 'recurring' && (
                      <ArrowPathIcon className="w-3 h-3 text-blue-500" title="Recurring Task" />
                    )}
                    {task.isRecurringInstance && task.type !== 'recurring' && (
                      <ArrowPathIcon className="w-3 h-3 text-green-500" title="Generated from Recurring Task" />
                    )}
                    {task.type === 'due' && (
                      <ClockIcon className="w-3 h-3" />
                    )}
                  </div>
                  {task.team && (
                    <div className="flex items-center space-x-1 mt-1">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: task.team.color }}
                      />
                      <span className="text-xs text-gray-600 truncate">{task.team.name}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            {tasksForDay.length > 3 && (
              <div className="text-xs text-gray-500 text-center py-1">
                +{tasksForDay.length - 3} more
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    return days;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div key={`admin-calendar-page-${selectedMember}-${monthData?.totalTasks || 0}`} className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <CalendarIcon className="w-8 h-8 text-primary-600" />
              <span>Admin Calendar</span>
            </h1>
            <p className="text-gray-600">
              View and manage all team tasks by date ({monthData?.totalTasks || 0} tasks this month)
            </p>
          </div>

          {/* Member Filter */}
          <MemberFilter
            users={users}
            selectedMember={selectedMember}
            onMemberChange={(memberId) => {
              console.log('🎯 [AdminCalendar] MemberFilter callback triggered with:', memberId);
              handleMemberChange(memberId);
            }}
            taskCount={monthData?.totalTasks || 0}
          />
          <div className="flex items-center space-x-3">
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => handleCreateTaskClick('')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Task
            </button>
          </div>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>

          <h2 className="text-xl font-semibold text-gray-900">
            {monthNames[currentMonth - 1]} {currentYear}
          </h2>

          <button
            onClick={() => navigateMonth('next')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div
          key={`admin-calendar-${selectedMember}-${currentYear}-${currentMonth}`}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50">
            {dayNames.map((day) => (
              <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {renderCalendarDays()}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <span>High Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <span>Medium Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span>Low Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-3 h-3 text-gray-600" />
              <span>Due Date</span>
            </div>
            <div className="flex items-center space-x-2">
              <UserGroupIcon className="w-3 h-3 text-gray-600" />
              <span>Team Task</span>
            </div>
          </div>
        </div>

        {/* Task Detail Modal */}
        <TaskDetailModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdate={(updatedTask) => {
            // Refresh calendar data when task is updated
            loadMonthData();
          }}
        />

        {/* Task Creation Modal */}
        <TaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          creatorId={currentUser?.id}
          onSubmit={async (taskData) => {
            try {
              setIsTaskLoading(true);
              console.log('Creating task with data:', taskData);
              console.log('Current user:', currentUser);

              // For regular tasks, ensure creatorId is included
              if (!taskData.isRecurring) {
                const taskDataWithCreator = {
                  ...taskData,
                  creatorId: currentUser?.id || 'cmdd8jvdu0001v1hsfzjmmox6' // Fallback to John Manager
                };
                console.log('Task data with creator:', taskDataWithCreator);
                await tasksAPI.create(taskDataWithCreator);
              } else {
                // For recurring tasks, the creatorId is already included in recurringTaskData
                console.log('Creating recurring task:', taskData);
                await tasksAPI.create(taskData);
              }
              handleTaskCreated();
            } catch (error: any) {
              console.error('Error creating task:', error);
              console.error('Error details:', error.response?.data);
              toast.error('Failed to create task: ' + (error.response?.data?.message || error.message));
            } finally {
              setIsTaskLoading(false);
            }
          }}
          teams={teams}
          users={users}
          projects={projects}
          isLoading={isTaskLoading}
          initialData={{
            dueDate: selectedDate ? new Date(selectedDate).toISOString() : undefined,
            startDate: selectedDate ? new Date(selectedDate).toISOString() : undefined,
          }}
        />

        {/* Day Tasks Modal */}
        <DayTasksModal
          isOpen={isDayTasksModalOpen}
          onClose={() => setIsDayTasksModalOpen(false)}
          date={selectedDayDate}
          tasks={selectedDayTasks}
          onTaskClick={(task) => {
            setSelectedTask(task);
            setIsTaskModalOpen(true);
            setIsDayTasksModalOpen(false);
          }}
          selectedMember={selectedMember}
          users={users}
        />

        {/* Loading Overlay for Task Operations */}
        {isTaskLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg">
              <LoadingSpinner size="lg" />
              <p className="mt-2 text-sm text-gray-600">Updating task...</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminCalendar;
